import AwsS3 from '@uppy/aws-s3';
import { Uppy, UppyFile } from '@uppy/core';
import { useCallback, useState } from 'react';
import { UploadContext } from './useUppyAwsS3UploadParameters';
import useUppyAwsS3UploadParameters from './useUppyAwsS3UploadParameters';
import { ONE_GIGABYTE_IN_BYTES } from 'utils/constants';
import { uploadErrorSentry } from './utils';

interface UseFileUploaderResponse {
  upload(file: File): Promise<UppyFile>;
}

export function useSimpleFileUploader(
  uploadContext: UploadContext,
): UseFileUploaderResponse {
  const buildGetUploadParameters = useUppyAwsS3UploadParameters();

  const [uppy] = useState(() => {
    const uppyInstance = new Uppy({
      meta: { type: 'avatar' },
      debug: true,
      restrictions: {
        maxFileSize: ONE_GIGABYTE_IN_BYTES,
      },
      autoProceed: true,
    });

    uppyInstance.use(AwsS3, {
      getUploadParameters: buildGetUploadParameters({
        uppy: uppyInstance,
        uploadContext,
      }),
    });
    return uppyInstance;
  });

  const upload = useCallback(
    (file: File) => {
      return new Promise<UppyFile>((resolve, reject) => {
        try {
          const fileId = uppy.addFile({
            data: file,
            name: file.name,
            type: file.type,
            meta: {
              relativePath: `allow_duplicates/${Date.now()}`,
            },
          });
          /* Resolve promise only after upload progress is completed */
          const uploadSuccess = (uppyFile: UppyFile | undefined) => {
            if (uppyFile?.id === fileId) {
              resolve(uppyFile);
            }
          };
          uppy.on('upload-success', uploadSuccess);
          const uploadError = (_: UppyFile | undefined, error: Error) => {
            reject(error);
            uploadErrorSentry(error);
          };
          uppy.on('upload-error', uploadError);
        } catch (err) {
          reject(err);
          if (err instanceof Error) {
            uploadErrorSentry(err);
          }
        }
      });
    },
    [uppy],
  );

  return {
    upload,
  };
}
